import { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import { SkiPlan } from '../types';

interface PDFPreviewDialogProps {
  plan: SkiPlan;
  previewImage: string;
  onClose: () => void;
  onConfirmExport: (includeMetadata: boolean, includeLegend: boolean) => void;
}

export function PDFPreviewDialog({ plan, previewImage, onClose, onConfirmExport }: PDFPreviewDialogProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeLegend, setIncludeLegend] = useState(true);

  useEffect(() => {
    // Simulate loading time for preview
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg">Aperçu du PDF</h2>
            <p className="text-sm text-gray-600 mt-1">
              {plan.name || 'Plan de Course - Ski de Fond'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Génération de l'aperçu...</p>
              </div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-5xl">
            <div className="relative w-full bg-gray-100 rounded-lg shadow-lg overflow-hidden" style={{ height: '70vh' }}>
              <img 
                src={previewImage} 
                alt="Aperçu PDF" 
                className="absolute inset-0 w-full h-full object-contain"
                draggable={false}
              />
            </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              <p>Format : A4 paysage</p>
              <p className="text-xs mt-1">
                {plan.elements.length} élément(s) • {plan.paths.length} tracé(s)
              </p>
            </div>
            
            {/* Options d'export */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeMetadata"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="includeMetadata" className="text-sm text-gray-700 cursor-pointer">
                  Inclure les informations de la course
                </label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeLegend"
                  checked={includeLegend}
                  onChange={(e) => setIncludeLegend(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="includeLegend" className="text-sm text-gray-700 cursor-pointer">
                  Afficher la légende
                </label>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => onConfirmExport(includeMetadata, includeLegend)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger le PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}